import React from 'react';
import { useTranslation } from 'react-i18next';

import { FormItem, useFormInstance, useWatchForm } from '@/components';
import { Input, Radio, RadioGroup, Select } from '@/ui';
import { IPartnerProfile } from '@/models/partners.model';

import { OwnershipTypes, PartnerFormFieldsNames, PaymentTargetTypes } from '../../helpers/types';
import { getTaxTypeOptions } from '../../helpers/getOptions';
import { usePartnerFormRules } from '../../hooks';
import TaxCodeField from '../TaxCodeField/TaxCodeField';
import { CompanyFields, CompanyRow, TaxTypeFormItem } from './CompanyDataFieldsStyles';

interface ICompanyDataFields {
  isNew?: boolean;
  initialValues?: IPartnerProfile | null;
}

const CompanyDataFields = ({ isNew = false }: ICompanyDataFields) => {
  const { t } = useTranslation(['partners', 'common']);
  const form = useFormInstance();

  const ownership = useWatchForm(['companyDraft', 'ownership'], form);
  const vatPayer = useWatchForm(['companyDraft', 'vatPayer'], form);

  const rules = usePartnerFormRules();
  const requiredRule = rules[PartnerFormFieldsNames.TITLE][0];
  const placeholder = t('common_placeholder_input', { ns: 'common' });

  return (
    <CompanyFields>
      <FormItem name={PartnerFormFieldsNames.TITLE} label={t('partners_company')} rules={rules.title}>
        <Input placeholder={placeholder} />
      </FormItem>

      <CompanyRow>
        <FormItem
          name={['companyDraft', 'ownership']}
          label={t('partners_ownership')}
          rules={isNew || ownership !== undefined ? rules.title : []}
        >
          <RadioGroup>
            <Radio value={OwnershipTypes.LEGAL_ENTITY}>{t('partners_legal_entity')}</Radio>
            <Radio value={OwnershipTypes.FOP}>{t('partners_company_fop')}</Radio>
          </RadioGroup>
        </FormItem>

        <TaxCodeField
          key={ownership === OwnershipTypes.FOP ? 'drfo' : 'edrpou'}
          kind={ownership === OwnershipTypes.FOP ? 'drfo' : 'edrpou'}
          required={ownership !== undefined}
        />
      </CompanyRow>

      <CompanyRow>
        <FormItem
          name={['companyDraft', 'vatPayer']}
          label={t('partners_vat_payer')}
          rules={isNew || vatPayer !== undefined ? rules.title : []}
        >
          <RadioGroup>
            <Radio value={true}>{t('partners_yes')}</Radio>
            <Radio value={false}>{t('partners_no')}</Radio>
          </RadioGroup>
        </FormItem>

        {vatPayer === false ? (
          <FormItem label={t('partners_ipn')}>
            <Input value={t('partners_not_vat_payer') as string} disabled />
          </FormItem>
        ) : (
          <TaxCodeField kind="ipn" required={vatPayer === true} />
        )}
      </CompanyRow>

      <CompanyRow>
        <FormItem
          name={PartnerFormFieldsNames.LEGAL_TYPE}
          label={t('partners_company_payment_target')}
          rules={rules[PartnerFormFieldsNames.LEGAL_TYPE]}
        >
          <RadioGroup>
            <Radio value={PaymentTargetTypes.TOV}>{t('partners_company_tov')}</Radio>
            <Radio value={PaymentTargetTypes.FOP}>{t('partners_company_fop')}</Radio>
          </RadioGroup>
        </FormItem>

        <TaxTypeFormItem
          name={PartnerFormFieldsNames.TAX_TYPE}
          label={t('partners_tax_type')}
          required
          rules={[requiredRule]}
        >
          <Select options={getTaxTypeOptions(t)} />
        </TaxTypeFormItem>
      </CompanyRow>

      <FormItem
        name={PartnerFormFieldsNames.LEGAL_ADDRESS}
        label={t('partners_legal_address')}
        required
        rules={rules.legal_address}
      >
        <Input placeholder={placeholder} />
      </FormItem>

      <FormItem
        name={PartnerFormFieldsNames.PHYSICAL_ADDRESS}
        label={t('partners_physical_address')}
        required
        rules={rules.physical_address}
      >
        <Input placeholder={placeholder} />
      </FormItem>
    </CompanyFields>
  );
};

export default CompanyDataFields;
